/**
 * NotificationQueue — Toast notification stack with animations.
 *
 * Replaces single-message HUD.showMessage with a proper queue.
 * Multiple notifications stack vertically without overlapping.
 * Each has its own fade-in/out animation.
 *
 * Types:
 * - info (blue)
 * - success (green)
 * - warning (yellow)
 * - error (red)
 * - achievement (gold with glow)
 * - system (gray)
 */

import { animations } from './AnimationSystem.js';

const TYPE_STYLES = {
  info:        { border: '#4488FF', bg: 'rgba(30,50,80,0.92)',  icon: 'ℹ️' },
  success:     { border: '#4CAF50', bg: 'rgba(30,60,30,0.92)',  icon: '✓' },
  warning:     { border: '#FFC107', bg: 'rgba(80,60,0,0.92)',   icon: '⚠' },
  error:       { border: '#F44336', bg: 'rgba(80,20,20,0.92)',  icon: '✕' },
  achievement: { border: '#FFD700', bg: 'rgba(60,50,10,0.92)',  icon: '🏆' },
  system:      { border: '#888888', bg: 'rgba(30,30,30,0.92)',  icon: '💾' },
};

export class NotificationQueue {
  constructor() {
    this._container = document.createElement('div');
    this._container.style.cssText = `
      position: fixed;
      top: 80px;
      right: 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      z-index: 200;
      pointer-events: none;
      max-width: 340px;
    `;
    document.body.appendChild(this._container);
    this._notifications = [];
  }

  /**
   * Show a notification.
   * @param {string} text
   * @param {'info'|'success'|'warning'|'error'|'achievement'|'system'} type
   * @param {number} duration seconds
   */
  push(text, type = 'info', duration = 3) {
    const style = TYPE_STYLES[type] || TYPE_STYLES.info;
    const el = document.createElement('div');
    el.style.cssText = `
      background: ${style.bg};
      border-left: 4px solid ${style.border};
      padding: 10px 14px;
      color: #FFF;
      font-family: 'Courier New', monospace;
      font-size: 13px;
      text-shadow: 1px 1px 0 rgba(0,0,0,0.6);
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
      pointer-events: auto;
      display: flex;
      align-items: center;
      gap: 10px;
      max-width: 100%;
      word-wrap: break-word;
    `;
    el.innerHTML = `
      <span style="font-size:18px;flex-shrink:0;">${style.icon}</span>
      <span style="flex:1;line-height:1.3;">${text}</span>
    `;

    // Animate in
    el.classList.add('fw-anim-slide-left');
    if (type === 'achievement') el.classList.add('fw-anim-glow');

    this._container.appendChild(el);
    const entry = { el, timer: duration };
    this._notifications.push(entry);

    // Click to dismiss
    el.addEventListener('click', () => this._remove(entry));

    // Auto-dismiss
    setTimeout(() => this._remove(entry), duration * 1000);

    return entry;
  }

  _remove(entry) {
    if (!entry.el || entry.el.parentNode !== this._container) return;
    animations.animateClose(entry.el, () => {
      if (entry.el.parentNode) entry.el.remove();
    }, 'fade');
    const idx = this._notifications.indexOf(entry);
    if (idx >= 0) this._notifications.splice(idx, 1);
  }

  clear() {
    for (const n of this._notifications) {
      if (n.el.parentNode) n.el.remove();
    }
    this._notifications = [];
  }

  // Shortcut methods
  info(text, duration)    { return this.push(text, 'info', duration); }
  success(text, duration) { return this.push(text, 'success', duration); }
  warning(text, duration) { return this.push(text, 'warning', duration); }
  error(text, duration)   { return this.push(text, 'error', duration); }
  achievement(text, duration) { return this.push(text, 'achievement', duration || 5); }
  system(text, duration)  { return this.push(text, 'system', duration || 1.5); }
}

// Singleton
export const notifications = new NotificationQueue();
